import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Globe, Mail, Phone, MapPin, Check } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { PageLoader } from '@/components/ui/Spinner';
import { useSiteSettings, useUpdateSiteSettings } from '@/hooks/useSiteSettings';
import { useTranslation } from '@/features/i18n';

type SettingsFormData = {
  site_name: string;
  site_description?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_address?: string;
  social_linkedin?: string;
  social_twitter?: string;
  social_facebook?: string;
  social_instagram?: string;
  footer_text?: string;
  logo_url?: string;
  logo_dark_url?: string;
};

export function SettingsPage() {
  const { data: settings, isLoading } = useSiteSettings();
  const updateSettings = useUpdateSiteSettings();
  const [saved, setSaved] = useState(false);
  const { t } = useTranslation();

  const settingsSchema = z.object({
    site_name: z.string().min(1, t.admin.settings.siteNameRequired),
    site_description: z.string().optional(),
    contact_email: z.string().email(t.admin.settings.emailInvalid).optional().or(z.literal('')),
    contact_phone: z.string().optional(),
    contact_address: z.string().optional(),
    social_linkedin: z.string().url().optional().or(z.literal('')),
    social_twitter: z.string().url().optional().or(z.literal('')),
    social_facebook: z.string().url().optional().or(z.literal('')),
    social_instagram: z.string().url().optional().or(z.literal('')),
    footer_text: z.string().optional(),
    logo_url: z.string().url().optional().or(z.literal('')),
    logo_dark_url: z.string().url().optional().or(z.literal('')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    values: settings ? {
      site_name: settings.site_name,
      site_description: settings.site_description || '',
      contact_email: settings.contact_email || '',
      contact_phone: settings.contact_phone || '',
      contact_address: settings.contact_address || '',
      social_linkedin: settings.social_linkedin || '',
      social_twitter: settings.social_twitter || '',
      social_facebook: settings.social_facebook || '',
      social_instagram: settings.social_instagram || '',
      footer_text: settings.footer_text || '',
      logo_url: settings.logo_url || '',
      logo_dark_url: settings.logo_dark_url || '',
    } : undefined,
  });

  const onSubmit = async (data: SettingsFormData) => {
    await updateSettings.mutateAsync(data);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <SEO title={t.admin.settings.title} noIndex />

      <div className="max-w-2xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{t.admin.settings.title}</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            {t.admin.settings.subtitle}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* General */}
          <Card>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <Globe className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <CardTitle>{t.admin.settings.general}</CardTitle>
                <CardDescription>{t.admin.settings.generalDesc}</CardDescription>
              </div>
            </div>

            <div className="space-y-4">
              <Input
                label={t.admin.settings.siteName}
                error={errors.site_name?.message}
                {...register('site_name')}
              />
              <Textarea
                label={t.admin.settings.siteDescription}
                rows={2}
                {...register('site_description')}
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  label={t.admin.settings.logoUrl}
                  placeholder="https://..."
                  {...register('logo_url')}
                />
                <Input
                  label={t.admin.settings.logoDarkUrl}
                  placeholder="https://..."
                  {...register('logo_dark_url')}
                />
              </div>
            </div>
          </Card>

          {/* Contact */}
          <Card>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <Mail className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <CardTitle>{t.admin.settings.contact}</CardTitle>
                <CardDescription>{t.admin.settings.contactDesc}</CardDescription>
              </div>
            </div>

            <div className="space-y-4">
              <Input
                label={t.admin.settings.email}
                type="email"
                leftIcon={<Mail className="w-4 h-4" />}
                {...register('contact_email')}
              />
              <Input
                label={t.admin.settings.phone}
                leftIcon={<Phone className="w-4 h-4" />}
                {...register('contact_phone')}
              />
              <Input
                label={t.admin.settings.address}
                leftIcon={<MapPin className="w-4 h-4" />}
                {...register('contact_address')}
              />
            </div>
          </Card>

          {/* Social */}
          <Card>
            <div className="flex items-start gap-4 mb-6">
              <div>
                <CardTitle>{t.admin.settings.social}</CardTitle>
                <CardDescription>{t.admin.settings.socialDesc}</CardDescription>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label={t.admin.settings.linkedin}
                placeholder="https://linkedin.com/company/..."
                {...register('social_linkedin')}
              />
              <Input
                label={t.admin.settings.twitter}
                placeholder="https://twitter.com/..."
                {...register('social_twitter')}
              />
              <Input
                label={t.admin.settings.facebook}
                placeholder="https://facebook.com/..."
                {...register('social_facebook')}
              />
              <Input
                label={t.admin.settings.instagram}
                placeholder="https://instagram.com/..."
                {...register('social_instagram')}
              />
            </div>
          </Card>

          {/* Footer */}
          <Card>
            <CardTitle className="mb-4">{t.admin.settings.footer}</CardTitle>
            <Input
              label={t.admin.settings.footerText}
              hint={t.admin.settings.footerHint}
              {...register('footer_text')}
            />
          </Card>

          <div className="flex justify-end">
            <Button
              type="submit"
              isLoading={isSubmitting}
              leftIcon={saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            >
              {saved ? t.admin.settings.saved : t.admin.settings.saveSettings}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
